/**
 * workcellTransferStateMachine.ts
 *
 * Event-Driven State Machines for:
 * 1. Big External Lifter (Overhead Track Port <-> Equipment Transfer Position)
 * 2. Internal Equipment Lifter (Equipment Transfer Position <-> Storage FOUP / Processing FOUP)
 *
 * Enforces strict event-driven triggers with no automatic cascading.
 * All motions are smooth, non-snapping, with proper dwell, holder engagement, and return-to-home.
 */

export type BigLifterState =
  | 'IDLE'
  | 'EVENT_RECEIVED'
  | 'VALIDATE_FOUP'
  | 'MOVE_TO_PICK_POSITION'
  | 'PICK_FOUP'
  | 'FOUP_SECURED'
  | 'MOVE_TO_TRANSFER_POS'
  | 'ALIGN_WITH_DESTINATION'
  | 'PLACE_FOUP'
  | 'RELEASE_FOUP'
  | 'TRANSFER_COMPLETE'
  | 'RETURN_TO_HOME';

export type InternalLifterState =
  | 'IDLE'
  | 'EVENT_RECEIVED'
  | 'VALIDATE_FOUP'
  | 'MOVE_TO_PICK_POSITION'
  | 'PICK_FOUP'
  | 'FOUP_SECURED'
  | 'MOVE_TO_DESTINATION'
  | 'ALIGN_WITH_DESTINATION'
  | 'PLACE_FOUP'
  | 'RELEASE_FOUP'
  | 'TRANSFER_COMPLETE'
  | 'RETURN_TO_HOME';

export type WorkcellFoupDestination = 'STORAGE' | 'PROCESSING' | 'TRANSFER_POS';

export type WorkcellTransferEvent =
  | { type: 'BIG_LIFTER_DOWN'; foupId?: string }
  | { type: 'BIG_LIFTER_UP'; foupId?: string }
  | { type: 'FOUP_TRANSFER_TO_STORAGE'; foupId?: string }
  | { type: 'FOUP_TRANSFER_TO_PROCESSING'; foupId?: string }
  | { type: 'FOUP_TRANSFER_TO_TRANSFER_POS'; foupId?: string; from: 'STORAGE' | 'PROCESSING' }
  | { type: 'RESET' };

export type WorkcellFoupLocation =
  | 'TRACK_PORT'
  | 'BIG_LIFTER_CARRIAGE'
  | 'EQUIPMENT_TRANSFER_POS'
  | 'INTERNAL_LIFTER_CARRIAGE'
  | 'STORAGE_FOUP'
  | 'PROCESSING_FOUP'
  | 'NONE';

export interface WorkcellTransferSnapshot {
  bigLifterState: BigLifterState;
  bigLifterProgress01: number; // 0 (home/top) -> 1 (docked/transfer pos)
  bigLifterHolderEngaged: boolean;
  
  internalLifterState: InternalLifterState;
  internalLifterProgress01: number; // 0..1 along motion path
  internalLifterDestination: WorkcellFoupDestination | null;
  internalLifterHolderEngaged: boolean;
  
  foupLocation: WorkcellFoupLocation;
  foupPosition: [number, number, number]; // 3D world/local coordinate
  foupSecured: boolean;
  
  activeEvent: string | null;
}

/** Smoothstep interpolation (smooth acceleration and deceleration) */
export function smoothstep(min: number, max: number, value: number): number {
  const x = Math.max(0, Math.min(1, (value - min) / (max - min)));
  return x * x * (3 - 2 * x);
}

/** Quintic smooth interpolation (zero 1st & 2nd derivatives at ends for ultra-smooth robotic motion) */
export function smootherstep(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return c * c * c * (c * (c * 6 - 15) + 10);
}

export interface WorkcellGeometricStations {
  trackPortY: number;
  transferPosY: number;
  chamberFloorY: number;
  
  transferPos: [number, number, number];
  storagePos: [number, number, number];
  processingPos: [number, number, number];
  internalLifterHomePos: [number, number, number];
}

export interface BigLifterPoseResult {
  carriageY: number;
  holderEngaged: boolean;
  foupY: number;
  foupLocation: WorkcellFoupLocation;
  state: BigLifterState;
  topShuttleZ: number;
  topHoistDropY: number;
  topFoupVisible: boolean;
  topFoupY: number;
  carriageFoupVisible: boolean;
}

/**
 * Computes the continuous pose of the Big Lifter and FOUP during a Big Lifter operation.
 * Implements seamless two-stage transfer:
 * Stage 1: Top Transfer Shuttle (Top Frame) <-> Track OHT
 * Stage 2: Vertical Lifter Carriage (Glass Tower) <-> Lower Load Port Area
 */
export function computeBigLifterPose(
  progress01: number,
  operation: 'DOWN' | 'UP',
  stations: WorkcellGeometricStations
): BigLifterPoseResult {
  const p = Math.max(0, Math.min(1, progress01));
  const trackY = stations.trackPortY;
  const transferY = stations.transferPosY;
  const stroke = trackY - transferY;
  const extendDist = 2.1; // Distance from lifter top crown to overhead track rail stop node

  let state: BigLifterState = 'IDLE';
  let carriageY = transferY;
  let holderEngaged = false;
  let foupY = transferY;
  let foupLocation: WorkcellFoupLocation = operation === 'DOWN' ? 'TRACK_PORT' : 'EQUIPMENT_TRANSFER_POS';

  let topShuttleZ = 0.0;
  let topHoistDropY = 0.0;
  let topFoupVisible = false;
  let topFoupY = trackY;
  let carriageFoupVisible = false;

  if (operation === 'DOWN') {
    // ── DROP SEQUENCE: 
    // 1. Top Shuttle Tray extends to Track Stop Node under OHT (VALIDATE_FOUP)
    // 2. Collects FOUP from OHT (PICK_FOUP)
    // 3. Retracts back to Lifter Crown and descends with FOUP into Cleanroom Chamber (MOVE_TO_TRANSFER_POS)
    // 4. Places FOUP onto Lower Stage (PLACE_FOUP -> RELEASE_FOUP)
    if (p < 0.10) {
      state = 'VALIDATE_FOUP';
      const u = smootherstep(p / 0.10);
      topShuttleZ = u * extendDist;
      carriageY = trackY;
      holderEngaged = false;
      foupY = trackY;
      foupLocation = 'TRACK_PORT';
      topFoupVisible = false;
      carriageFoupVisible = false;
    } else if (p < 0.22) {
      state = 'PICK_FOUP';
      topShuttleZ = extendDist;
      carriageY = trackY;
      holderEngaged = true;
      foupLocation = 'BIG_LIFTER_CARRIAGE';
      topFoupVisible = true;
      carriageFoupVisible = false;
      foupY = trackY;
    } else if (p < 0.78) {
      state = 'MOVE_TO_TRANSFER_POS';
      const u = (p - 0.22) / (0.78 - 0.22);
      if (u < 0.25) {
        // Horizontal retraction stage back to shaft center
        const retU = smootherstep(u / 0.25);
        topShuttleZ = (1 - retU) * extendDist;
        topFoupVisible = true;
        carriageFoupVisible = false;
        carriageY = trackY;
      } else {
        // Vertical descent stage
        topShuttleZ = 0.0;
        topFoupVisible = false;
        carriageFoupVisible = true;
        const vertU = smootherstep((u - 0.25) / 0.75);
        carriageY = trackY - vertU * stroke;
      }
      foupY = carriageY;
      foupLocation = 'BIG_LIFTER_CARRIAGE';
      holderEngaged = true;
    } else if (p < 0.88) {
      state = 'PLACE_FOUP';
      topShuttleZ = 0.0;
      carriageY = transferY;
      carriageFoupVisible = true;
      topFoupVisible = false;
      foupY = transferY;
      foupLocation = 'EQUIPMENT_TRANSFER_POS';
      holderEngaged = true;
    } else {
      state = 'RELEASE_FOUP';
      topShuttleZ = 0.0;
      carriageY = transferY;
      carriageFoupVisible = true;
      topFoupVisible = false;
      foupY = transferY;
      foupLocation = 'EQUIPMENT_TRANSFER_POS';
      holderEngaged = false;
    }
  } else {
    // ── PICK SEQUENCE:
    // 1. Vertical Carriage ascends with FOUP from Chamber to Lifter Crown
    // 2. FOUP transfers to Top Shuttle Tray
    // 3. Top Shuttle Tray extends to Track Stop Node under OHT
    // 4. OHT receives FOUP
    // 5. Empty Shuttle Tray retracts back to home
    if (p < 0.12) {
      state = 'MOVE_TO_PICK_POSITION';
      carriageY = transferY;
      carriageFoupVisible = true;
      topFoupVisible = false;
      topShuttleZ = 0.0;
      foupY = transferY;
      foupLocation = 'EQUIPMENT_TRANSFER_POS';
      holderEngaged = false;
    } else if (p < 0.24) {
      state = 'PICK_FOUP';
      carriageY = transferY;
      holderEngaged = true;
      foupLocation = 'BIG_LIFTER_CARRIAGE';
      carriageFoupVisible = true;
      topFoupVisible = false;
      topShuttleZ = 0.0;
      foupY = transferY;
    } else if (p < 0.70) {
      state = 'MOVE_TO_TRANSFER_POS';
      const u = (p - 0.24) / (0.70 - 0.24);
      if (u < 0.65) {
        // Vertical ascent stage
        const vertU = smootherstep(u / 0.65);
        carriageY = transferY + vertU * stroke;
        topShuttleZ = 0.0;
        carriageFoupVisible = true;
        topFoupVisible = false;
      } else {
        // Horizontal extension stage to track stop node under OHT
        const extU = smootherstep((u - 0.65) / 0.35);
        topShuttleZ = extU * extendDist;
        carriageY = trackY;
        carriageFoupVisible = false;
        topFoupVisible = true;
      }
      foupY = carriageY;
      foupLocation = 'BIG_LIFTER_CARRIAGE';
      holderEngaged = true;
    } else if (p < 0.82) {
      state = 'PLACE_FOUP';
      topShuttleZ = extendDist;
      carriageY = trackY;
      topFoupVisible = false;
      carriageFoupVisible = false;
      foupLocation = 'TRACK_PORT';
      foupY = trackY;
      holderEngaged = true;
    } else if (p < 0.88) {
      state = 'TRANSFER_COMPLETE';
      const u = smootherstep((p - 0.82) / 0.06);
      topShuttleZ = (1 - u) * extendDist;
      carriageY = trackY;
      topFoupVisible = false;
      carriageFoupVisible = false;
      foupLocation = 'TRACK_PORT';
      foupY = trackY;
      holderEngaged = false;
    } else {
      state = 'TRANSFER_COMPLETE';
      topShuttleZ = 0.0;
      const u = smootherstep((p - 0.88) / 0.12);
      carriageY = trackY - u * stroke;
      topFoupVisible = false;
      carriageFoupVisible = false;
      foupLocation = 'TRACK_PORT';
      foupY = trackY;
      holderEngaged = false;
    }
  }

  return {
    carriageY,
    holderEngaged,
    foupY,
    foupLocation,
    state,
    topShuttleZ,
    topHoistDropY,
    topFoupVisible,
    topFoupY,
    carriageFoupVisible,
  };
}

/**
 * Computes the continuous pose of the Internal Equipment Lifter during an internal transfer.
 */
export function computeInternalLifterPose(
  progress01: number,
  destination: WorkcellFoupDestination,
  stations: WorkcellGeometricStations
): {
  armPosition: [number, number, number];
  holderEngaged: boolean;
  foupPosition: [number, number, number];
  foupLocation: WorkcellFoupLocation;
  state: InternalLifterState;
} {
  const p = Math.max(0, Math.min(1, progress01));
  const startPos = stations.transferPos;
  const targetPos = destination === 'STORAGE'
    ? stations.storagePos
    : destination === 'PROCESSING'
      ? stations.processingPos
      : stations.transferPos;

  // Phase breakdown for Internal Lifter:
  // 0.00..0.12: MOVE_TO_PICK_POSITION (move to equipment transfer position)
  // 0.12..0.24: PICK_FOUP / HOLDER ENGAGE (clamps FOUP)
  // 0.24..0.72: MOVE_TO_DESTINATION (smooth horizontal/vertical transfer path)
  // 0.72..0.84: ALIGN_WITH_DESTINATION / PLACE_FOUP
  // 0.84..0.92: RELEASE_FOUP (unclamp)
  // 0.92..1.00: RETURN_TO_HOME / COMPLETE

  let state: InternalLifterState = 'IDLE';
  let armPosition: [number, number, number] = [...stations.internalLifterHomePos];
  let holderEngaged = false;
  let foupPosition: [number, number, number] = [...startPos];
  let foupLocation: WorkcellFoupLocation = 'EQUIPMENT_TRANSFER_POS';

  if (p < 0.12) {
    state = 'MOVE_TO_PICK_POSITION';
    const subP = smootherstep(p / 0.12);
    armPosition = [
      stations.internalLifterHomePos[0] + (startPos[0] - stations.internalLifterHomePos[0]) * subP,
      stations.internalLifterHomePos[1] + (startPos[1] - stations.internalLifterHomePos[1]) * subP,
      stations.internalLifterHomePos[2] + (startPos[2] - stations.internalLifterHomePos[2]) * subP,
    ];
    holderEngaged = false;
    foupPosition = [...startPos];
    foupLocation = 'EQUIPMENT_TRANSFER_POS';
  } else if (p < 0.24) {
    state = 'PICK_FOUP';
    armPosition = [...startPos];
    holderEngaged = true;
    foupPosition = [...startPos];
    foupLocation = 'INTERNAL_LIFTER_CARRIAGE';
  } else if (p < 0.72) {
    state = 'MOVE_TO_DESTINATION';
    const travelP = smootherstep((p - 0.24) / (0.72 - 0.24));
    // Midpoint arch elevation for collision-free transfer
    const archY = Math.sin(travelP * Math.PI) * 1.2;
    armPosition = [
      startPos[0] + (targetPos[0] - startPos[0]) * travelP,
      startPos[1] + (targetPos[1] - startPos[1]) * travelP + archY,
      startPos[2] + (targetPos[2] - startPos[2]) * travelP,
    ];
    holderEngaged = true;
    foupPosition = [...armPosition];
    foupLocation = 'INTERNAL_LIFTER_CARRIAGE';
  } else if (p < 0.84) {
    state = 'PLACE_FOUP';
    armPosition = [...targetPos];
    holderEngaged = true;
    foupPosition = [...targetPos];
    foupLocation = destination === 'STORAGE' ? 'STORAGE_FOUP' : 'PROCESSING_FOUP';
  } else if (p < 0.92) {
    state = 'RELEASE_FOUP';
    armPosition = [...targetPos];
    holderEngaged = false;
    foupPosition = [...targetPos];
    foupLocation = destination === 'STORAGE' ? 'STORAGE_FOUP' : 'PROCESSING_FOUP';
  } else {
    state = 'RETURN_TO_HOME';
    const homeP = smootherstep((p - 0.92) / (1.00 - 0.92));
    armPosition = [
      targetPos[0] + (stations.internalLifterHomePos[0] - targetPos[0]) * homeP,
      targetPos[1] + (stations.internalLifterHomePos[1] - targetPos[1]) * homeP,
      targetPos[2] + (stations.internalLifterHomePos[2] - targetPos[2]) * homeP,
    ];
    holderEngaged = false;
    foupPosition = [...targetPos];
    foupLocation = destination === 'STORAGE' ? 'STORAGE_FOUP' : 'PROCESSING_FOUP';
  }

  return { armPosition, holderEngaged, foupPosition, foupLocation, state };
}
